<template>
    <div v-bind:class="{ 'container-fluid': !displayedInModal }">
        <h1>
            {{ title }}
            <em
                v-show="domain && domain.unavailable"
                class="error fas fa-exclamation-triangle"
                :title="$t('domainNotAvailable')"
            >
            </em>
            <!-- Close icon -->
            <icon-close
                :id="'domain-detail-close'"
                v-if="displayedInModal && isGenerating"
                @close="$_onCancel"
                iconColor=""
                class="d-inline-block float-right"
            >
            </icon-close>
        </h1>
        <br />
        <b-form
            ref="domainContainerForm"
            class="col-xs-12 col-md-12 pb-5"
            v-bind:class="{
                ' col-lg-12': displayedInModal,
                ' col-lg-8': !displayedInModal
            }"
            name="themeLayerForm"
            v-on:submit.prevent="$_onSubmit"
        >
            <domain-detail-form
                :displayedInModal="displayedInModal"
                ref="form"
                v-on:unavailable="$_setUnavailable"
                :tableResultSearchAttribute="tableResultSearchAttribute"
            >
            </domain-detail-form>
            <!-- save/cancel/remove buttons -->
            <submit-cancel-remove
                :is-saving="isSaving"
                :is-deleting="isDeleting || isGenerating"
                :class="{ 'modal-position': displayedInModal }"
                :mode="modeEditConst"
                @cancel="$_onCancel"
                @remove="$_onRemove"
            >
            </submit-cancel-remove>
        </b-form>
    </div>
</template>

<script>
    import { MODE_ADD, MODE_EDIT } from '../../constants';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import DomainDetailForm from './DomainDetailForm.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import IconClose from '../../components/IconClose.vue';

    export default {
        name: 'DomainDetail',

        mixins: [modalMixin],

        components: {
            'icon-close': IconClose,
            'domain-detail-form': DomainDetailForm,
            'submit-cancel-remove': SubmitCancelRemove
        },

        props: {
            id: { type: Number, required: false, default: -1 },
            displayedInModal: { type: Boolean, required: false, default: false },
            tableResultSearchAttribute: { type: Object, required: false, default: null }
        },

        data() {
            return {
                // Constants
                modeEditConst: MODE_EDIT,

                title: '',

                // add/edit mode
                mode: 0,

                // state
                isSaving: false,
                isDeleting: false,
                isGenerating: false,
                domain: { unavailable: false }
            };
        },

        mounted() {
            if (this.id > 0) {
                this.mode = MODE_EDIT;
                this.title = this.$t('domainEditTitle');
            } else {
                this.mode = MODE_ADD;
                this.title = this.$t('domainNewTitle');
            }
            const domainForm = this.$refs.form;
            domainForm.initialize(this.id);
            this.$root.$on('domain-generating', (value) => {
                this.isGenerating = value.isGenerating;
                this.domain.isGenerating = value.isGenerating;
                if (value.domain) {
                    this.domain = value.domain;
                }
            });
            this.$root.$on('generated-domain', (value) => {
                this.$emit('generated-domain', value);
            });
        },

        methods: {
            $_createDomainInUseError(domainInUseResult) {
                if (domainInUseResult.inUse) {
                    let message = '';
                    if (domainInUseResult.inUseInFeedbackField) {
                        domainInUseResult.usedInFeedbackFieldNames.forEach(
                            (feedbackFieldName) => {
                                // Solving Sonar qube notification
                                const errorMessage = this.$t(
                                    'domainInUseFeedbackNames',
                                    [
                                        `${feedbackFieldName.feedbackName}`,
                                        `${feedbackFieldName.feedbackFieldName}`
                                    ]
                                );
                                message += `<li>- ${errorMessage}</li >`;
                            }
                        );
                    }
                    if (domainInUseResult.inUseInFilter) {
                        domainInUseResult.usedInFilterNames.forEach(
                            (filterName) => {
                                // Solving Sonar qube notification
                                const errorMessage = this.$t(
                                    'domainInUseThemeNames',
                                    [
                                        `${filterName.themeName}`,
                                        `${filterName.themeLayerName}`,
                                        `${filterName.filterName}`
                                    ]
                                );
                                message += `<li>- ${errorMessage}</li >`;
                            }
                        );
                    }

                    const vNodesMsg = `${this.$t(
                        'domainInUseErrorMessage'
                    )} <ul>${message}</ul>`;
                    const data = {
                        message: [vNodesMsg],
                        variant: 'danger',
                        noCloseButton: true,
                        autoHideDelay: 8000
                    };
                    this.showToast(data);
                }
            },

            $_getFormattedFeedbackFieldNames(names) {
                const feedbackNamesMessage = this.$t('domainInUseFeedbackNames', [
                    `${names.feedbackName}`,
                    `${names.feedbackFieldName}`
                ]);
                return `- ${feedbackNamesMessage}`;
            },

            $_getFormattedFilterNames(names) {
                const themeNamesMessage = this.$t('domainInUseThemeNames', [
                    `${names.themeName}`,
                    `${names.themeLayerName}`,
                    `${names.filterName}`
                ]);
                return `- ${themeNamesMessage}`;
            },

            /**
             * Domain form event handler sets unavailable flag
             * @param  {bool} value [true if unavailable, false otherwise]
             */
            $_setUnavailable(value) {
                this.domain.unavailable = value;
            },

            /**
             * Goto the overview form
             */
            $_gotoOverview() {
                this.$router.push({ path: '/dashboard/domains' });
            },

            $_onSubmit() {
                if (this.isGenerating) {
                    if (this.displayedInModal) {
                        this.$_notifyDomainGenerationSubscribers(this.domain);
                    } else {
                        this.$_gotoOverview();
                    }
                } else {
                    this.$_onSave();
                }
            },

            /**
             * Save changes
             */
            $_onSave() {
                const domainForm = this.$refs.form;
                const valid = domainForm.$_onAddNewKeyValue();

                if (valid) {
                    this.isSaving = true;
                    // provide this as a parameter
                    domainForm
                        .save(domainForm)
                        .then((result) => {
                            this.isSaving = false;

                            const data = {
                                message: this.$t('domainCardSavedConfirmation'),
                                variant: 'success',
                                autoHideDelay: 2500,
                                noCloseButton: true
                            };
                            this.showToast(data);

                            if (result.status === 200 && this.mode === MODE_ADD) {
                                // Don't navigate to overview when domain is added by modal
                                if (this.displayedInModal) {
                                    result.data.isGenerating = false;
                                    this.$_notifyDomainGenerationSubscribers(
                                        result.data
                                    );
                                } else {
                                    this.$_gotoOverview();
                                }
                            }
                        })
                        .catch((error) => {
                            this.isSaving = false;
                            // Disply unexpected error in pop-up
                            if (
                                error.message &&
                                error.message.startsWith('Foutmelding')
                            ) {
                                const data = {
                                    title: this.$t('saveFailureTitle'),
                                    message: error,
                                    variant: 'danger',
                                    autoHideDelay: 10000
                                };
                                this.showToast(data);
                            } else {
                                // Display required error template
                                const data = {
                                    message: this.$t('errorRequiredFieldsUnset'),
                                    variant: 'danger',
                                    noCloseButton: true,
                                    autoHideDelay: 8000
                                };
                                this.showToast(data);
                            }
                        });
                }
            },

            $_onCancel() {
                // Don't navigate to overview when domain is add by modal
                if (this.displayedInModal) {
                    this.$emit('domain-cancel');
                } else {
                    this.$_gotoOverview();
                }
            },

            $_onRemove() {
                const data = {
                    message: this.$t('domainCardDeletionsConfirmation')
                };
                this.msgBoxConfirm(data).then(async (confirmed) => {
                    if (confirmed) {
                        this.isDeleting = true;
                        await this.$store.dispatch('useApiData', {
                            name: 'GetDomainInUse',
                            parameters: { id: this.id }
                        }).then(
                                async (result) => {
                                    if (result.data.inUse) {
                                        this.$_createDomainInUseError(result.data);
                                        this.isDeleting = false;
                                    } else {
                                        await this.$store.dispatch('useApiData', {
                                            name: 'DeleteFeedbackDomain',
                                            parameters: { id: this.id }
                                        }).then(
                                                () => {
                                                    this.isDeleting = false;
                                                    this.$_gotoOverview();
                                                },
                                                (error) => {
                                                    this.isDeleting = false;
                                                    this.showToast({
                                                        title: this.$t(
                                                            'deleteFailureTitle'
                                                        ),
                                                        message: this.$t(
                                                            'deleteFailureMessage',
                                                            [
                                                                `${error.response.data.message}`
                                                            ]
                                                        ),
                                                        variant: 'danger',
                                                        autoHideDelay: 10000
                                                    });
                                                }
                                            );
                                    }
                                },
                                (error) => {
                                    this.isDeleting = false;
                                }
                            );
                    }
                });
            },

            $_notifyDomainGenerationSubscribers(domain) {
                this.$emit('domain-saved', domain);
            }
        }
    };
</script>

<style scoped>
.error {
    color: red;
}

li {
    margin-left: 20px;
}

.modal-position {
    padding-left: 15px;
}
</style>
